import { TermsDocumentPublic } from '../../../types';

interface TermsProgressHeaderProps {
  documents: TermsDocumentPublic[];
  activeIndex: number;
  agreements: Map<string, boolean>;
  isSummaryView: boolean;
  isArabic: boolean;
  onChipClick: (index: number) => void;
  labels: {
    docOfN: (current: number, total: number, title: string) => string;
    finalReview: string;
    chipLockedAria: string;
    chipAgreedAria: string;
    chipCurrentAria: string;
  };
}

export default function TermsProgressHeader({
  documents,
  activeIndex,
  agreements,
  isSummaryView,
  isArabic,
  onChipClick,
  labels,
}: TermsProgressHeaderProps) {
  const totalDocs = documents.length;
  const allAgreed = totalDocs > 0 && documents.every((d) => agreements.get(d.id));

  const currentTitle = (() => {
    if (isSummaryView) return labels.finalReview;
    const doc = documents[activeIndex];
    if (!doc) return '';
    return isArabic && doc.titleAr ? doc.titleAr : doc.title;
  })();

  const headlineText = isSummaryView
    ? labels.finalReview
    : labels.docOfN(activeIndex + 1, totalDocs, currentTitle);

  return (
    <div className="terms-progress-header" aria-live="polite">
      <div className="terms-progress-headline">{headlineText}</div>
      <div className="terms-progress-chips" role="list" dir={isArabic ? 'rtl' : 'ltr'}>
        {documents.map((doc, index) => {
          const isAgreed = agreements.get(doc.id) ?? false;
          const isCurrent = !isSummaryView && index === activeIndex;
          const everyPriorAgreed = documents
            .slice(0, index)
            .every((d) => agreements.get(d.id));
          const isReachable = isAgreed || isCurrent || everyPriorAgreed;

          const cls = [
            'terms-progress-chip',
            isCurrent ? 'is-current' : '',
            isAgreed ? 'is-agreed' : '',
            !isReachable ? 'is-locked' : '',
          ]
            .filter(Boolean)
            .join(' ');

          const ariaLabel = isCurrent
            ? labels.chipCurrentAria
            : isAgreed
              ? labels.chipAgreedAria
              : labels.chipLockedAria;

          return (
            <button
              key={doc.id}
              type="button"
              role="listitem"
              className={cls}
              disabled={!isReachable}
              aria-label={`${ariaLabel}: ${isArabic && doc.titleAr ? doc.titleAr : doc.title}`}
              aria-current={isCurrent ? 'step' : undefined}
              onClick={() => isReachable && onChipClick(index)}
            >
              <span className="terms-progress-chip-num">
                {isAgreed ? (
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                ) : (
                  index + 1
                )}
              </span>
            </button>
          );
        })}

        <button
          type="button"
          role="listitem"
          className={[
            'terms-progress-chip',
            'terms-progress-chip-final',
            isSummaryView ? 'is-current' : '',
            !allAgreed ? 'is-locked' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          disabled={!allAgreed}
          aria-label={labels.finalReview}
          onClick={() => allAgreed && onChipClick(totalDocs)}
        >
          <span className="terms-progress-chip-num" aria-hidden="true">
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M9 11l3 3L22 4" />
              <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
            </svg>
          </span>
        </button>
      </div>
    </div>
  );
}
